/**
 * src/data/EmotionalIntelligenceBank.js
 * Hardcoded scenario bank for Module 4: Emotional Intelligence (Fragment Synthesis)
 */

export const eqBank = [
  {
    "id": "eq_01",
    "incomingMessage": "MANAGER: 'Your recent formatting error broke the master financial spreadsheet. The formulas are completely wiped out. Fix it now.'",
    "availableChips": [
      "I am restoring the previous version now.",
      "The accounting team missed it too.",
      "This wasn't my fault.",
      "I will audit the damaged tabs",
      "and repair the broken formulas",
      "You're overreacting.",
      "I didn't have enough time.",
      "within the hour."
    ],
    "optimalSequence": [
      "I am restoring the previous version now.",
      "I will audit the damaged tabs",
      "and repair the broken formulas",
      "within the hour."
    ],
    "failureFeedback": "Defensive Escalation Bias. You interpreted a direct operational failure as an opportunity to pass blame. Deflecting to the accounting team or complaining about time demonstrates a lack of extreme ownership and makes you uncoachable."
  },
  {
    "id": "eq_02",
    "incomingMessage": "PEER: 'You completely overwrote my slide deck for the client presentation. Why didn't you check version history?'",
    "availableChips": [
      "Your version was outdated.",
      "Don't talk to me like that.",
      "My apologies.",
      "I will restore your version immediately",
      "You should have locked the file.",
      "and manually merge my updates",
      "This is a team effort.",
      "so we don't lose your work."
    ],
    "optimalSequence": [
      "My apologies.",
      "I will restore your version immediately",
      "and manually merge my updates",
      "so we don't lose your work."
    ],
    "failureFeedback": "Defensive Escalation Bias. Blaming the peer for not locking the file or invalidating their work to protect your own ego destroys horizontal trust and collaboration."
  },
  {
    "id": "eq_03",
    "incomingMessage": "MANAGER: 'You missed the deadline for the compliance audit submission. This puts the whole department at risk.'",
    "availableChips": [
      "Legal didn't get back to me.",
      "I take full responsibility.",
      "I have too much on my plate.",
      "I am uploading the documents now",
      "This is an unfair expectation.",
      "and will schedule a post-mortem",
      "I'm doing the best I can.",
      "to ensure this never happens again."
    ],
    "optimalSequence": [
      "I take full responsibility.",
      "I am uploading the documents now",
      "and will schedule a post-mortem",
      "to ensure this never happens again."
    ],
    "failureFeedback": "Defensive Escalation Bias. Externalizing blame to Legal or complaining about workload during a compliance failure shows zero executive accountability and a victim mindset."
  },
  {
    "id": "eq_04",
    "incomingMessage": "PEER: 'Your copy for the new ad campaign is really generic. It doesn't align with the brand voice at all.'",
    "availableChips": [
      "Thanks for the direct feedback.",
      "I worked really hard on this.",
      "Can you point out a specific line",
      "The CMO approved it.",
      "that feels off-brand",
      "You're not a copywriter.",
      "so I can recalibrate?",
      "That's just your opinion."
    ],
    "optimalSequence": [
      "Thanks for the direct feedback.",
      "Can you point out a specific line",
      "that feels off-brand",
      "so I can recalibrate?"
    ],
    "failureFeedback": "Defensive Escalation Bias. Taking creative feedback personally or pulling rank ('The CMO approved it') shuts down horizontal collaboration and reveals a fragile ego."
  },
  {
    "id": "eq_05",
    "incomingMessage": "MANAGER: 'You were completely unprepared for that client call. You didn't know the basic account history.'",
    "availableChips": [
      "The account manager didn't brief me.",
      "They asked trick questions.",
      "You are absolutely right.",
      "I will build a preparation checklist",
      "I think it went fine.",
      "for all future calls",
      "I was hired for strategy, not history.",
      "to ensure I know the account history."
    ],
    "optimalSequence": [
      "You are absolutely right.",
      "I will build a preparation checklist",
      "for all future calls",
      "to ensure I know the account history."
    ],
    "failureFeedback": "Defensive Escalation Bias. Shifting blame to the account manager or minimizing the failure ('I think it went fine') displays toxic denial and a refusal to learn."
  },
  {
    "id": "eq_06",
    "incomingMessage": "PEER: 'You promised the client three extra deliverables we don't have the capacity to provide. You just threw the Operations team under the bus.'",
    "availableChips": [
      "Sales is about saying yes.",
      "Operations just needs to work faster.",
      "I own that mistake.",
      "I will call the client back today",
      "I'll talk to my VP about this.",
      "to reset the scope boundaries",
      "Don't be so dramatic.",
      "and protect our team's capacity."
    ],
    "optimalSequence": [
      "I own that mistake.",
      "I will call the client back today",
      "to reset the scope boundaries",
      "and protect our team's capacity."
    ],
    "failureFeedback": "Defensive Escalation Bias. Justifying scope creep ('Sales is about saying yes') or antagonizing Operations destroys cross-functional alignment and system health."
  },
  {
    "id": "eq_07",
    "incomingMessage": "MANAGER: 'I left 40 comments on your strategic brief. The logic is full of holes and it lacks data. Rewrite it.'",
    "availableChips": [
      "Got it.",
      "This is micromanagement.",
      "I will review your comments,",
      "I disagree with your edits.",
      "incorporate the hard data,",
      "I guess I can't do anything right.",
      "and send a revised draft tomorrow.",
      "Nobody else complains about my writing."
    ],
    "optimalSequence": [
      "Got it.",
      "I will review your comments,",
      "incorporate the hard data,",
      "and send a revised draft tomorrow."
    ],
    "failureFeedback": "Defensive Escalation Bias. Viewing heavy redlines as 'micromanagement' or getting passive-aggressive proves you have a fragile ego and an inability to iterate."
  },
  {
    "id": "eq_08",
    "incomingMessage": "PEER: 'During the team meeting, you presented the Q3 market research report as if you built it alone. I did 80% of that data analysis.'",
    "availableChips": [
      "I was just summarizing.",
      "You are right, and I apologize.",
      "You're being overly sensitive.",
      "I will send a correction in the team channel",
      "I'm the project lead.",
      "to explicitly credit your data analysis",
      "I didn't mean it like that.",
      "and ensure it doesn't happen again."
    ],
    "optimalSequence": [
      "You are right, and I apologize.",
      "I will send a correction in the team channel",
      "to explicitly credit your data analysis",
      "and ensure it doesn't happen again."
    ],
    "failureFeedback": "Defensive Escalation Bias. Gaslighting the peer or using hierarchical titles ('I'm the project lead') to justify stealing credit destroys psychological safety."
  },
  {
    "id": "eq_09",
    "incomingMessage": "MANAGER: 'You've been 15 minutes late to the daily sync three times this week. It's disrespectful to the team's time.'",
    "availableChips": [
      "Traffic has been bad.",
      "My previous meeting runs over.",
      "I apologize for the recurring issue.",
      "I will adjust my morning schedule",
      "I'm still getting my work done.",
      "and ensure I am online and prepped",
      "It's only 15 minutes.",
      "five minutes before the sync."
    ],
    "optimalSequence": [
      "I apologize for the recurring issue.",
      "I will adjust my morning schedule",
      "and ensure I am online and prepped",
      "five minutes before the sync."
    ],
    "failureFeedback": "Defensive Escalation Bias. Making excuses (traffic, other meetings) or minimizing the impact signals a lack of basic professional respect and accountability."
  },
  {
    "id": "eq_10",
    "incomingMessage": "PEER: 'I've been waiting on your budget approvals for 48 hours. You are officially blocking the entire procurement team.'",
    "availableChips": [
      "I have other priorities.",
      "You didn't remind me.",
      "I apologize for the bottleneck.",
      "I am prioritizing the approvals right now",
      "Finance takes time.",
      "and will have them to you",
      "Stop rushing me.",
      "by 2 PM today."
    ],
    "optimalSequence": [
      "I apologize for the bottleneck.",
      "I am prioritizing the approvals right now",
      "and will have them to you",
      "by 2 PM today."
    ],
    "failureFeedback": "Defensive Escalation Bias. Invalidating the peer's timeline or shifting the blame to a lack of reminders shows terrible time management and teamwork."
  },
  {
    "id": "eq_11",
    "incomingMessage": "MANAGER: 'Your department is $20k over budget for Q2. You clearly aren't tracking your vendor spend.'",
    "availableChips": [
      "Finance didn't warn me.",
      "I missed the variance.",
      "The vendors raised their prices.",
      "I will audit the vendor invoices today",
      "We needed the tools to succeed.",
      "and present a cost-reduction plan",
      "I'm not an accountant.",
      "to balance the Q3 ledger."
    ],
    "optimalSequence": [
      "I missed the variance.",
      "I will audit the vendor invoices today",
      "and present a cost-reduction plan",
      "to balance the Q3 ledger."
    ],
    "failureFeedback": "Defensive Escalation Bias. Claiming ignorance or blaming Finance for your own departmental budget failure shows a severe lack of executive ownership."
  },
  {
    "id": "eq_12",
    "incomingMessage": "PEER: 'The tone you used with me on the client call was incredibly condescending. Don't speak to me like that again.'",
    "availableChips": [
      "I was just being direct.",
      "I am truly sorry.",
      "You're taking it the wrong way.",
      "It was unprofessional of me.",
      "I was under a lot of stress.",
      "I will be far more mindful of my tone",
      "HR would disagree.",
      "moving forward."
    ],
    "optimalSequence": [
      "I am truly sorry.",
      "It was unprofessional of me.",
      "I will be far more mindful of my tone",
      "moving forward."
    ],
    "failureFeedback": "Defensive Escalation Bias. Using the 'I was just being direct' defense to mask condescension, or tone-policing the victim, shows terrible emotional intelligence."
  },
  {
    "id": "eq_13",
    "incomingMessage": "MANAGER: 'That presentation to the board was disorganized. You rambled and failed to answer their core financial questions.'",
    "availableChips": [
      "They kept interrupting me.",
      "I understand and agree.",
      "The slides were fine.",
      "I will partner with Finance",
      "I think you're being harsh.",
      "to tighten the narrative",
      "I wasn't given enough prep time.",
      "before the next quarterly review."
    ],
    "optimalSequence": [
      "I understand and agree.",
      "I will partner with Finance",
      "to tighten the narrative",
      "before the next quarterly review."
    ],
    "failureFeedback": "Defensive Escalation Bias. Blaming the board for interrupting or complaining about prep time demonstrates a complete inability to manage up and take executive feedback."
  },
  {
    "id": "eq_14",
    "incomingMessage": "PEER: 'You have ignored my last three emails about the Q4 roadmap. I cannot do my job if you refuse to communicate.'",
    "availableChips": [
      "I get 500 emails a day.",
      "I apologize for the silence.",
      "It wasn't a high priority.",
      "I dropped the ball.",
      "You could have slacked me.",
      "Let's jump on a 10-minute call",
      "Calm down.",
      "so I can unblock you immediately."
    ],
    "optimalSequence": [
      "I apologize for the silence.",
      "I dropped the ball.",
      "Let's jump on a 10-minute call",
      "so I can unblock you immediately."
    ],
    "failureFeedback": "Defensive Escalation Bias. Telling a blocked peer to 'calm down' or blaming your high email volume is dismissive and toxic behavior."
  },
  {
    "id": "eq_15",
    "incomingMessage": "MANAGER: 'There are typos and formatting errors all over this public-facing press release. It looks completely amateurish.'",
    "availableChips": [
      "Marketing was supposed to review it.",
      "I will take it down immediately,",
      "I was rushing to meet your deadline.",
      "fix all formatting errors,",
      "Nobody reads these anyway.",
      "and run it through a peer review",
      "You're nitpicking.",
      "before republishing."
    ],
    "optimalSequence": [
      "I will take it down immediately,",
      "fix all formatting errors,",
      "and run it through a peer review",
      "before republishing."
    ],
    "failureFeedback": "Defensive Escalation Bias. Calling quality control 'nitpicking' or blaming the deadline for your own lack of attention to detail proves you lack pride in your work."
  },
  {
    "id": "eq_16",
    "incomingMessage": "PEER: 'In the review meeting, you blamed my team for the delayed launch, but your missing compliance paperwork caused the roadblock.'",
    "availableChips": [
      "We share the blame.",
      "You are absolutely right.",
      "Your processes are too fragile.",
      "I reacted defensively in the meeting.",
      "I didn't explicitly say your name.",
      "I will formally clarify the root cause",
      "Let's just move on.",
      "and take ownership of the paperwork delay."
    ],
    "optimalSequence": [
      "You are absolutely right.",
      "I reacted defensively in the meeting.",
      "I will formally clarify the root cause",
      "and take ownership of the paperwork delay."
    ],
    "failureFeedback": "Defensive Escalation Bias. Attempting to diffuse the blame after you wrongly accused someone else shows a profound lack of integrity and accountability."
  },
  {
    "id": "eq_17",
    "incomingMessage": "MANAGER: 'You lost the enterprise deal because you refused to negotiate on the implementation fee. You read the room completely wrong.'",
    "availableChips": [
      "They were never going to buy.",
      "I miscalculated their threshold.",
      "Our fees are too high anyway.",
      "I will document the learnings from this loss",
      "I was following standard protocol.",
      "and adjust my pricing strategy",
      "I disagree with your assessment.",
      "for the upcoming pipeline."
    ],
    "optimalSequence": [
      "I miscalculated their threshold.",
      "I will document the learnings from this loss",
      "and adjust my pricing strategy",
      "for the upcoming pipeline."
    ],
    "failureFeedback": "Defensive Escalation Bias. Blaming the prospect or stubbornly defending your protocol instead of analyzing your own failure to negotiate shows a fixed, uncoachable mindset."
  },
  {
    "id": "eq_18",
    "incomingMessage": "PEER: 'You left me off the invite list for the product strategy kickoff. My department is directly impacted by this.'",
    "availableChips": [
      "We wanted to keep the group small.",
      "That was an oversight on my part.",
      "It was just an initial brainstorm.",
      "I apologize.",
      "You can catch up later.",
      "I am forwarding the invite now",
      "I thought someone else invited you.",
      "and will send you the pre-read materials."
    ],
    "optimalSequence": [
      "That was an oversight on my part.",
      "I apologize.",
      "I am forwarding the invite now",
      "and will send you the pre-read materials."
    ],
    "failureFeedback": "Defensive Escalation Bias. Justifying the exclusion with 'keeping the group small' invalidates the peer's legitimate need to be involved and reinforces silos."
  },
  {
    "id": "eq_19",
    "incomingMessage": "MANAGER: 'Your output has dropped significantly over the last month. You are missing your KPIs across the board.'",
    "availableChips": [
      "The KPIs are unrealistic.",
      "I acknowledge the drop in output.",
      "I'm burnt out because of this company.",
      "I have been struggling with prioritization.",
      "I'm doing more than everyone else.",
      "Could we review my current task list",
      "This feels like a PIP setup.",
      "to help me refocus?"
    ],
    "optimalSequence": [
      "I acknowledge the drop in output.",
      "I have been struggling with prioritization.",
      "Could we review my current task list",
      "to help me refocus?"
    ],
    "failureFeedback": "Defensive Escalation Bias. Reacting with paranoia ('PIP setup') or claiming you work harder than others deflects from the objective reality of your missed KPIs."
  },
  {
    "id": "eq_20",
    "incomingMessage": "PEER: 'You reached out directly to my client without looping me in. That undermines my relationship with them.'",
    "availableChips": [
      "I was just trying to help.",
      "You are right, I overstepped.",
      "The client liked my idea.",
      "I apologize for bypassing you.",
      "We all work for the same company.",
      "I will ensure all future communication",
      "You were taking too long to reply.",
      "goes directly through you."
    ],
    "optimalSequence": [
      "You are right, I overstepped.",
      "I apologize for bypassing you.",
      "I will ensure all future communication",
      "goes directly through you."
    ],
    "failureFeedback": "Defensive Escalation Bias. Justifying territorial overreach with 'We all work for the same company' destroys internal protocol and horizontal trust."
  },
  {
    "id": "eq_21",
    "incomingMessage": "MANAGER: 'You took yesterday off without logging it in the HR system or getting approval. This is unacceptable.'",
    "availableChips": [
      "I told my team on Slack.",
      "I take full responsibility.",
      "I worked extra hours last week.",
      "I will log it immediately",
      "It was a personal emergency.",
      "and ensure all future PTO",
      "I didn't think it was a big deal.",
      "is submitted properly in advance."
    ],
    "optimalSequence": [
      "I take full responsibility.",
      "I will log it immediately",
      "and ensure all future PTO",
      "is submitted properly in advance."
    ],
    "failureFeedback": "Defensive Escalation Bias. Explaining that you 'told the team on Slack' shows you do not respect official compliance systems or management oversight."
  },
  {
    "id": "eq_22",
    "incomingMessage": "PEER: 'You keep interrupting people and dominating the conversation in the weekly syncs. It's making it hard for others to speak.'",
    "availableChips": [
      "I just have a lot of ideas.",
      "Thank you for calling me out.",
      "Nobody else is speaking up.",
      "I wasn't aware I was doing that.",
      "That's just my personality.",
      "I will step back in future meetings",
      "You're stifling innovation.",
      "to ensure others have the floor."
    ],
    "optimalSequence": [
      "Thank you for calling me out.",
      "I wasn't aware I was doing that.",
      "I will step back in future meetings",
      "to ensure others have the floor."
    ],
    "failureFeedback": "Defensive Escalation Bias. Using 'it's just my personality' or blaming others for being quiet is classic toxic behavior that ruins meeting equity."
  },
  {
    "id": "eq_23",
    "incomingMessage": "MANAGER: 'You CC'd the wrong client on an email and leaked confidential pricing data. This is a severe compliance breach.'",
    "availableChips": [
      "The email addresses auto-filled.",
      "I own this critical error.",
      "It's not that much of a leak.",
      "I am notifying Security right now",
      "I was rushing to meet quota.",
      "and drafting a retraction email",
      "What's the big deal?",
      "for Legal to review."
    ],
    "optimalSequence": [
      "I own this critical error.",
      "I am notifying Security right now",
      "and drafting a retraction email",
      "for Legal to review."
    ],
    "failureFeedback": "Defensive Escalation Bias. Blaming software ('auto-fill') for a compliance breach demonstrates a catastrophic lack of accountability during a crisis."
  },
  {
    "id": "eq_24",
    "incomingMessage": "PEER: 'You sent the mass email without running a test send and broke the formatting for everyone.'",
    "availableChips": [
      "The test sends take too long.",
      "That is entirely my fault.",
      "I thought it was a minor typo fix.",
      "I am stopping the email sequence now",
      "Someone else approved the draft.",
      "and will run a full rendering test",
      "Don't yell at me.",
      "before sending any more messages."
    ],
    "optimalSequence": [
      "That is entirely my fault.",
      "I am stopping the email sequence now",
      "and will run a full rendering test",
      "before sending any more messages."
    ],
    "failureFeedback": "Defensive Escalation Bias. Blaming the person who approved the draft or complaining about test duration shows you are a liability to the operational pipeline."
  },
  {
    "id": "eq_25",
    "incomingMessage": "MANAGER: 'You threw your junior analyst under the bus in front of the client to save your own skin. That is unacceptable leadership.'",
    "availableChips": [
      "The analyst actually made the mistake.",
      "I failed as a leader today.",
      "I was protecting the agency's reputation.",
      "I will apologize to the analyst privately,",
      "They need to learn accountability.",
      "and follow up with the client",
      "It wasn't a big deal.",
      "to take full responsibility for the error."
    ],
    "optimalSequence": [
      "I failed as a leader today.",
      "I will apologize to the analyst privately,",
      "and follow up with the client",
      "to take full responsibility for the error."
    ],
    "failureFeedback": "Defensive Escalation Bias. Doubling down by saying the analyst made the mistake proves you do not understand the concept of 'Extreme Ownership' in leadership."
  },
  {
    "id": "eq_26",
    "incomingMessage": "INTERNAL THOUGHT: 'It's been 3 days since the interview. They are ghosting me. I need to send an aggressive follow-up right now.'",
    "availableChips": [
      "I will wait until Tuesday morning",
      "I am sending an email now",
      "to check in professionally",
      "to demand an update",
      "because they are busy, not malicious.",
      "They are disrespecting my time.",
      "I deserve an answer.",
      "Desperation smells bad."
    ],
    "optimalSequence": [
      "I will wait until Tuesday morning",
      "to check in professionally",
      "because they are busy, not malicious."
    ],
    "failureFeedback": "Defensive Escalation Bias. Panicking and sending an aggressive 'bubbling this up' email after only 3 days makes you look desperate and destroys your leverage."
  }
];